import { useCallback, useEffect, useRef, useState } from 'react';
import {
    DEFAULT_ARTICLES_PER_SECURITY,
    fetchSecurityArticles,
} from '../services/newsService';

/**
 * Per-security article-detail hook — Stage 2 of the two-stage news load
 * (Fix 2, 2026-08-12).
 *
 * `loadDetails(cogniveId)` fetches full articles (title, summary, publisher,
 * url, yahoo_url) for one security on demand — typically triggered when the
 * user opens that security's news popover. Results are cached per Cognive ID for
 * `cacheTtlMs` (5 minutes by default) so reopening the popover within the
 * window makes no additional request.
 *
 * `enabled` mirrors `useWatchlistNews` and is set from the watchlist view's
 * visibility. While it is false the hook issues no requests, and the edge
 * into false abandons whatever is already in flight — navigating away from
 * the Watchlist during a detail fetch must never write that response into
 * state on the way back.
 *
 * Concurrency guarantees:
 *   - Multiple `loadDetails(cogniveId)` calls for the same identity that arrive
 *     while a request is in flight share the same promise; only one HTTP
 *     request is issued.
 *   - Stale responses (unmount, userUid change, leaving the Watchlist) never
 *     write to state.
 *   - Successful cache entries survive across `loadDetails` calls until
 *     the TTL expires or the hook unmounts / user changes. Leaving the
 *     Watchlist deliberately keeps the cache: coming back within the TTL
 *     should still be free.
 */

export const SECURITY_ARTICLES_CACHE_TTL_MS = 5 * 60 * 1000;

function normalizeCogniveId(raw) {
    if (raw === null || raw === undefined) return '';
    return String(raw).trim();
}

export default function useSecurityNewsDetails({
    userUid,
    enabled = true,
    limit = DEFAULT_ARTICLES_PER_SECURITY,
    cacheTtlMs = SECURITY_ARTICLES_CACHE_TTL_MS,
    // Injectable for tests. Defaults to the real service function.
    fetchArticles = fetchSecurityArticles,
} = {}) {
    // Per-identity cache: {articles, timestamp}. Not React state — mutating it
    // shouldn't cause re-renders, only per-identity status transitions do.
    const cacheRef = useRef(new Map());
    // In-flight promises, keyed by Cognive ID, so concurrent loadDetails() calls
    // for the same security share one HTTP request.
    const inflightRef = useRef(new Map());
    const mountedRef = useRef(true);
    // Bumped whenever we should discard in-flight responses (userUid change,
    // unmount, leaving the Watchlist). Compared inside the async task to guard
    // state writes.
    const generationRef = useRef(0);

    const active = Boolean(userUid && enabled);

    const [articlesByIdentity, setArticlesByIdentity] = useState({});
    const [loadingByIdentity, setLoadingByIdentity] = useState({});
    const [errorByIdentity, setErrorByIdentity] = useState({});

    // Reset on userUid change: forget any cached articles / in-flight promises
    // so a different user never sees another's cached results, and any late
    // response gets rejected by the generation guard below.
    useEffect(() => {
        cacheRef.current = new Map();
        inflightRef.current = new Map();
        generationRef.current += 1;
        setArticlesByIdentity({});
        setLoadingByIdentity({});
        setErrorByIdentity({});
    }, [userUid]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            // Invalidate every in-flight request so a late resolution can't
            // touch state after unmount.
            generationRef.current += 1;
        };
    }, []);

    // Leaving the Watchlist (or logging out) abandons every in-flight detail
    // request: bumping the generation makes their guards reject the state
    // write, and dropping the inflight map means a later visit starts a fresh
    // request rather than adopting an orphaned promise. The per-identity cache is
    // kept on purpose — returning within the TTL should still cost nothing.
    // Spinners for the abandoned requests are cleared; nothing will resolve
    // them.
    useEffect(() => {
        if (active) return;
        generationRef.current += 1;
        inflightRef.current = new Map();
        setLoadingByIdentity((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    }, [active]);

    const loadDetails = useCallback(async (rawCogniveId) => {
        const cogniveId = normalizeCogniveId(rawCogniveId);
        if (!cogniveId || !active) return null;

        // Cache hit within TTL → return cached articles without a request.
        const cached = cacheRef.current.get(cogniveId);
        if (cached && (Date.now() - cached.timestamp) < cacheTtlMs) {
            // Ensure state reflects the cached slice (in case another identity
            // was loading concurrently and this one hadn't been rendered yet).
            setArticlesByIdentity((prev) => (
                prev[cogniveId] === cached.articles
                    ? prev
                    : { ...prev, [cogniveId]: cached.articles }
            ));
            return cached.articles;
        }

        // Dedup: an in-flight request for the same identity is reused.
        const inflight = inflightRef.current.get(cogniveId);
        if (inflight) return inflight;

        const generation = generationRef.current;
        setLoadingByIdentity((prev) => ({ ...prev, [cogniveId]: true }));
        setErrorByIdentity((prev) => {
            if (!(cogniveId in prev)) return prev;
            const next = { ...prev };
            delete next[cogniveId];
            return next;
        });

        // A holder rather than a bare `let`, so `finally` can identify this
        // request even if `fetchArticles` throws synchronously and the block
        // runs before the assignment below completes.
        const entry = {};
        entry.promise = (async () => {
            try {
                const resp = await fetchArticles(cogniveId, limit);
                if (!mountedRef.current || generation !== generationRef.current) {
                    return null;
                }
                const articles = Array.isArray(resp?.articles) ? resp.articles : [];
                cacheRef.current.set(cogniveId, { articles, timestamp: Date.now() });
                setArticlesByIdentity((prev) => ({ ...prev, [cogniveId]: articles }));
                return articles;
            } catch (err) {
                if (!mountedRef.current || generation !== generationRef.current) {
                    return null;
                }
                console.error(`Failed to fetch articles for ${cogniveId}:`, err);
                setErrorByIdentity((prev) => ({ ...prev, [cogniveId]: err }));
                return null;
            } finally {
                // Only retract our own entry. After a disable/enable round trip
                // a newer request for the same identity may already own the key.
                if (
                    entry.promise
                    && inflightRef.current.get(cogniveId) === entry.promise
                ) {
                    inflightRef.current.delete(cogniveId);
                }
                if (mountedRef.current && generation === generationRef.current) {
                    setLoadingByIdentity((prev) => {
                        if (!(cogniveId in prev)) return prev;
                        const next = { ...prev };
                        delete next[cogniveId];
                        return next;
                    });
                }
            }
        })();

        inflightRef.current.set(cogniveId, entry.promise);
        return entry.promise;
    }, [active, limit, cacheTtlMs, fetchArticles]);

    return {
        articlesByIdentity,
        loadingByIdentity,
        errorByIdentity,
        loadDetails,
    };
}
